import React from "react";
import { Clock, MapPin, CalendarDays, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { CATEGORIES, STATUS, TYPE_LABEL, formatDatesIT } from "../lib/categories";
import { useSite } from "@/context/SiteData";

export const CategoryBadge = ({ categoryKey, small }) => {
  const { catMap } = useSite();
  const cat = catMap[categoryKey] || CATEGORIES[categoryKey];
  if (!cat) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-600 ${small ? "text-[11px] px-2.5 py-1" : "text-xs px-3 py-1.5"}`}
      style={{ background: `${cat.color}14`, color: cat.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: cat.color }} />
      {cat.label}
    </span>
  );
};

export const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.available;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-600 px-2.5 py-1"
      style={{ background: s.bg, color: s.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
      {s.label}
    </span>
  );
};

export const CourseCard = ({ course, index, onOpen }) => {
  const light = !!course.text_light;
  const bg = course.bg_color;
  const accentCls = light ? "text-white" : "text-[#EA6A0A]";
  const metaCls = light ? "text-white/80" : "text-gray-500";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: (index % 3) * 0.06 }}
      data-testid={`course-card-${course.id}`}
      onClick={() => onOpen(course)}
      style={bg ? { background: bg } : undefined}
      className="bg-white rounded-3xl border border-gray-100 p-6 sm:p-7 flex flex-col relative transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] cursor-pointer group overflow-hidden"
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <CategoryBadge categoryKey={course.category} small />
        <StatusBadge status={course.status} />
      </div>

      <div className="flex items-center gap-2 mb-2">
        <span className={`text-[11px] font-600 uppercase tracking-wide ${light ? "text-white/60" : "text-gray-400"}`}>
          {TYPE_LABEL[course.type]}
        </span>
      </div>

      <h3 className={`font-display font-700 text-lg leading-snug tracking-tight transition-colors ${light ? "text-white group-hover:text-white" : "text-[#111827] group-hover:text-[#EA6A0A]"}`}>
        {course.name}
      </h3>

      <div className={`mt-5 space-y-2.5 text-sm ${metaCls}`}>
        <div className="flex items-center gap-2.5">
          <CalendarDays size={16} className={`${accentCls} shrink-0`} />
          <span className="line-clamp-1">{formatDatesIT(course.dates)}</span>
        </div>
        <div className="flex items-center gap-2.5">
          <Clock size={16} className={`${accentCls} shrink-0`} />
          <span>{course.duration_label}</span>
        </div>
        <div className="flex items-center gap-2.5">
          <MapPin size={16} className={`${accentCls} shrink-0`} />
          <span className="line-clamp-1">{course.location}</span>
        </div>
      </div>

      <div className={`mt-6 pt-5 border-t flex items-center justify-between ${light ? "border-white/20" : "border-gray-100"}`}>
        <div className="flex items-baseline gap-1">
          <span className={`font-display font-700 text-2xl ${accentCls}`}>€{course.price}</span>
          <span className={`text-xs ${light ? "text-white/60" : "text-gray-400"}`}>+ IVA</span>
        </div>
        <span
          data-testid={`course-open-${course.id}`}
          className={`inline-flex items-center gap-1.5 text-sm font-600 group-hover:gap-2.5 transition-all ${light ? "text-white" : "text-[#111827]"}`}
        >
          Scopri di più
          <ArrowUpRight size={16} />
        </span>
      </div>
    </motion.div>
  );
};
